'use client';

import { useMemo, useEffect, useState, useRef } from 'react';
import gsap from 'gsap/all';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { useTranslation, Container } from '@weareconceptstudio/core';

//* Libs
import { getProducts } from '@/libs/get-data';

//* Helpers
import { config } from '@/helpers';

//* Styles
import ProductsStyle from './style';

//* Components
import StyledQuotedText from '@/components/StyledQuotedText';
import Button from '@/components/Button';
import ProductItem from '@/modules/Products/components/List/Item';
import TitleAnim from '@/modules/_animation/Title';

gsap.registerPlugin(ScrollTrigger);

const Products = ({ id }) => {
	const { selectedLang } = useTranslation();

	//! State
	const [data, setData] = useState([]);

	//! Ref
	const buttonRefWrap = useRef();
	const triggerRef = useRef();
	const titleRef = useRef();
	const productWrapRef = useRef();

	useEffect(() => {
		getProducts({ currentProductId: id, lang: selectedLang, params: { rand: true } }).then((res) => setData(res?.data || []));
	}, [selectedLang, id]);

	//! News Item
	const dataProducts = useMemo(() => {
		return data?.map((item, i) => (
			<div
				key={i}
				className='related-product-wrapper opacity-zero'
			>
				<ProductItem {...item} />
			</div>
		));
	}, [data]);

	useEffect(() => {
		if (buttonRefWrap.current && data.length) {
			triggerRef.current = ScrollTrigger.create({
				start: 'top bottom',
				end: 'top top',
				trigger: titleRef.current,
				onEnter: () => {
					if (buttonRefWrap.current && productWrapRef.current?.children?.length) {
						gsap.fromTo(buttonRefWrap.current, { scale: 0 }, { scale: 1, duration: 0.6, ease: 'power2.inOut' });
						gsap.fromTo(productWrapRef.current?.children, { opacity: 0 }, { opacity: 1, duration: 0.4, ease: 'power2.in', stagger: 0.1 });
						gsap.fromTo(
							productWrapRef.current?.children,
							{
								y: (ind) => {
									return `${40 * (ind + 1)}%`;
								},
							},
							{ y: 0, duration: 0.6, ease: 'power2.out', stagger: 0.05 }
						);
					}
				},
				onLeaveBack: () => {
					if (buttonRefWrap.current && productWrapRef.current?.children?.length) {
						gsap.set(buttonRefWrap.current, { scale: 0 });
						gsap.set(productWrapRef.current?.children, {
							y: (ind) => {
								return `${40 * (ind + 1)}%`;
							},
							opacity: 0,
						});
					}
				},
			});
		}
		return () => {
			if (triggerRef.current) {
				triggerRef.current.kill();
			}
		};
	});

	return (
		<ProductsStyle>
			<Container
				isSection
				row
			>
				<div className={'col-1 col-ds-12'} />
				<div className={'col-10 col-ds-12 col-m-12'}>
					{dataProducts?.length > 0 && (
						<>
							<div className='wrapper-related-titles'>
								<div ref={titleRef}>
									<TitleAnim
										trigger={'bottom'}
										load={true}
									>
										<StyledQuotedText text={'our" ""products"'} />
									</TitleAnim>
								</div>
								<div
									ref={buttonRefWrap}
									className='wrap-button'
								>
									<Button
										url={`${config.routes.products.path}`}
										text={'viewAll'}
										className={'show-more-btn'}
									/>
								</div>
							</div>

							<div className='wrapper-items'>
								<div
									ref={productWrapRef}
									className={'wrapper-products'}
								>
									{dataProducts}
								</div>
							</div>
						</>
					)}
				</div>

				<div className={'col-1 col-ds-12'} />
			</Container>
		</ProductsStyle>
	);
};

export default Products;
